// =============================================================================
// SimSense Platform — Weather Tooltip Graphic
// =============================================================================
// OT-257: Visual weather widget for session card hover preview.
// Shows air temp, track temp, wind speed + direction, humidity, sky condition.

import type { ParsedWeather } from "../utils/weatherParser";
import { useUnits } from "../hooks/useUnits";
import "./WeatherTooltip.css";

interface WeatherTooltipProps {
  weather: ParsedWeather;
}

function skyIcon(label: string | null): string {
  if (!label) return "";
  const l = label.toLowerCase();
  if (l.includes("rain")) return "rain";
  if (l.includes("fog")) return "fog";
  if (l.includes("overcast")) return "overcast";
  if (l.includes("mostly")) return "mostly-cloudy";
  if (l.includes("partly")) return "partly-cloudy";
  if (l.includes("clear")) return "clear";
  return "clear";
}

function tempColor(celsius: number): string {
  if (celsius < 10) return "#60a5fa"; // cold blue
  if (celsius < 20) return "#34d399"; // cool green
  if (celsius < 30) return "#fbbf24"; // warm amber
  if (celsius < 40) return "#f97316"; // hot orange
  return "#ef4444"; // extreme red
}

function windArrowRotation(dir: string | null): number | null {
  // Arrow points in the direction the wind is coming FROM
  // e.g. "SW" wind → arrow points toward SW (down-left)
  if (!dir) return null;
  const map: Record<string, number> = {
    N: 0, NNE: 22.5, NE: 45, ENE: 67.5,
    E: 90, ESE: 112.5, SE: 135, SSE: 157.5,
    S: 180, SSW: 202.5, SW: 225, WSW: 247.5,
    W: 270, WNW: 292.5, NW: 315, NNW: 337.5,
  };
  return map[dir.toUpperCase()] ?? null;
}

function SkyGraphic({ condition }: { condition: string }) {
  if (condition === "rain") {
    return (
      <svg className="sky-icon" viewBox="0 0 40 40" fill="none">
        <path d="M12 22c-3.3 0-6-2.7-6-6s2.7-6 6-6c.5-3.4 3.5-6 7-6 3.9 0 7 3.1 7 7 0 .3 0 .7-.1 1 2.3.9 4.1 3.1 4.1 5.7 0 2.4-1.9 4.3-4.3 4.3H12z" fill="#64748b" />
        <line x1="12" y1="28" x2="10" y2="34" stroke="#60a5fa" strokeWidth="1.5" strokeLinecap="round" />
        <line x1="19" y1="28" x2="17" y2="34" stroke="#60a5fa" strokeWidth="1.5" strokeLinecap="round" />
        <line x1="26" y1="28" x2="24" y2="34" stroke="#60a5fa" strokeWidth="1.5" strokeLinecap="round" />
      </svg>
    );
  }
  if (condition === "overcast") {
    return (
      <svg className="sky-icon" viewBox="0 0 40 40" fill="none">
        <path d="M12 26c-3.3 0-6-2.7-6-6s2.7-6 6-6c.5-3.4 3.5-6 7-6 3.9 0 7 3.1 7 7 0 .3 0 .7-.1 1 2.3.9 4.1 3.1 4.1 5.7 0 2.4-1.9 4.3-4.3 4.3H12z" fill="#64748b" />
      </svg>
    );
  }
  if (condition === "mostly-cloudy") {
    return (
      <svg className="sky-icon" viewBox="0 0 40 40" fill="none">
        <circle cx="14" cy="14" r="6" fill="#fbbf24" />
        <path d="M16 28c-3.3 0-6-2.7-6-6s2.7-6 6-6c.5-3 3-5.3 6.2-5.3 3.4 0 6.2 2.8 6.2 6.2 0 .2 0 .5-.1.8 1.9.8 3.2 2.6 3.2 4.6 0 2-1.6 3.7-3.7 3.7H16z" fill="#94a3b8" />
      </svg>
    );
  }
  if (condition === "partly-cloudy") {
    return (
      <svg className="sky-icon" viewBox="0 0 40 40" fill="none">
        <circle cx="16" cy="14" r="7" fill="#fbbf24" />
        <path d="M18 30c-2.8 0-5-2.2-5-5s2.2-5 5-5c.4-2.6 2.6-4.5 5.2-4.5 2.9 0 5.2 2.3 5.2 5.2 0 .2 0 .5-.1.7 1.6.7 2.7 2.2 2.7 4 0 2-1.3 3.6-3.1 3.6H18z" fill="#94a3b8" opacity="0.7" />
      </svg>
    );
  }
  if (condition === "fog") {
    return (
      <svg className="sky-icon" viewBox="0 0 40 40" fill="none">
        <line x1="6" y1="16" x2="34" y2="16" stroke="#94a3b8" strokeWidth="2" strokeLinecap="round" />
        <line x1="10" y1="22" x2="30" y2="22" stroke="#94a3b8" strokeWidth="2" strokeLinecap="round" />
        <line x1="8" y1="28" x2="32" y2="28" stroke="#94a3b8" strokeWidth="2" strokeLinecap="round" />
      </svg>
    );
  }
  // Clear
  return (
    <svg className="sky-icon" viewBox="0 0 40 40" fill="none">
      <circle cx="20" cy="20" r="8" fill="#fbbf24" />
      {[0, 45, 90, 135, 180, 225, 270, 315].map((deg) => (
        <line
          key={deg}
          x1="20"
          y1="20"
          x2={20 + 13 * Math.cos((deg * Math.PI) / 180)}
          y2={20 + 13 * Math.sin((deg * Math.PI) / 180)}
          stroke="#fbbf24"
          strokeWidth="1.5"
          strokeLinecap="round"
        />
      ))}
    </svg>
  );
}

function WindCompass({ direction, speedLabel }: { direction: string | null; speedLabel: string }) {
  const rotation = windArrowRotation(direction);

  return (
    <div className="wind-compass">
      <svg viewBox="0 0 48 48" className="compass-svg">
        {/* Compass ring */}
        <circle cx="24" cy="24" r="20" fill="none" stroke="var(--border, #2a2f42)" strokeWidth="1.5" />
        {/* Cardinal markers */}
        <text x="24" y="8" className="compass-label" textAnchor="middle">N</text>
        <text x="24" y="44" className="compass-label" textAnchor="middle">S</text>
        <text x="5" y="27" className="compass-label" textAnchor="middle">W</text>
        <text x="43" y="27" className="compass-label" textAnchor="middle">E</text>
        {/* Arrow */}
        {rotation != null ? (
          <g transform={`rotate(${rotation}, 24, 24)`}>
            <polygon
              points="24,8 21,20 24,18 27,20"
              fill="var(--accent, #3b82f6)"
            />
            <line x1="24" y1="18" x2="24" y2="36" stroke="var(--accent, #3b82f6)" strokeWidth="1.5" strokeLinecap="round" />
          </g>
        ) : (
          <circle cx="24" cy="24" r="3" fill="var(--text3, #64748b)" />
        )}
      </svg>
      <div className="wind-speed-label">{speedLabel}</div>
      {direction && <div className="wind-dir-label">{direction}</div>}
    </div>
  );
}

export function WeatherTooltip({ weather }: WeatherTooltipProps) {
  const units = useUnits();
  const { airTempC, trackTempC, windMs, humidity, skyLabel, windDir } = weather;

  const hasData = skyLabel || airTempC != null || trackTempC != null || windMs != null;
  if (!hasData) return null;

  const condition = skyIcon(skyLabel);

  return (
    <div className="weather-tooltip">
      <div className="weather-tooltip-header">
        <span className="weather-tooltip-title">Conditions</span>
      </div>

      {/* Row 1: Sky + Temps — centered, labels on top */}
      <div style={{ display: "flex", justifyContent: "center", gap: "20px", alignItems: "flex-start", marginBottom: "10px" }}>
        <div style={{ textAlign: "center" }}>
          <SkyGraphic condition={condition} />
          <div style={{ fontSize: "11px", fontWeight: 600, color: "var(--text1)", marginTop: "2px" }}>{skyLabel || "—"}</div>
        </div>
        {airTempC != null && (
          <div style={{ textAlign: "center" }}>
            <div style={{ fontSize: "10px", color: "var(--text3)", textTransform: "uppercase", marginBottom: "4px" }}>Air</div>
            <div style={{ display: "flex", alignItems: "center", gap: "5px", justifyContent: "center" }}>
              <span className="temp-dot" style={{ backgroundColor: tempColor(airTempC) }} />
              <span className="temp-value">{units.formatTemp(airTempC)}</span>
            </div>
          </div>
        )}
        {trackTempC != null && (
          <div style={{ textAlign: "center" }}>
            <div style={{ fontSize: "10px", color: "var(--text3)", textTransform: "uppercase", marginBottom: "4px" }}>Track</div>
            <div style={{ display: "flex", alignItems: "center", gap: "5px", justifyContent: "center" }}>
              <span className="temp-dot" style={{ backgroundColor: tempColor(trackTempC) }} />
              <span className="temp-value">{units.formatTemp(trackTempC)}</span>
            </div>
          </div>
        )}
      </div>

      {/* Row 2: Wind + Humidity — centered */}
      {(windMs != null || humidity != null) && (
        <div style={{ display: "flex", justifyContent: "center", alignItems: "center", gap: "20px" }}>
          {windMs != null && (
            <WindCompass
              direction={windDir}
              speedLabel={units.formatSpeed(windMs)}
            />
          )}
          {humidity != null && (
            <div style={{ textAlign: "center" }}>
              <div style={{ fontSize: "10px", color: "var(--text3)", textTransform: "uppercase", marginBottom: "4px" }}>Humidity</div>
              <div style={{ display: "flex", alignItems: "center", gap: "4px", justifyContent: "center" }}>
                <svg viewBox="0 0 16 16" width="14" height="14">
                  <path d="M8 2 C8 2 3 8 3 11 C3 13.76 5.24 14 8 14 C10.76 14 13 13.76 13 11 C13 8 8 2 8 2z" fill="var(--accent, #3b82f6)" opacity="0.3" />
                  <path d="M8 2 C8 2 3 8 3 11 C3 13.76 5.24 14 8 14 C10.76 14 13 13.76 13 11 C13 8 8 2 8 2z" fill="none" stroke="var(--accent, #3b82f6)" strokeWidth="1" />
                </svg>
                <span style={{ fontSize: "13px", fontWeight: 600, color: "var(--text1)" }}>{Math.round(humidity)}%</span>
              </div>
            </div>
          )}
        </div>
      )}
    </div>
  );
}
